import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Paper, { PaperProps } from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useLoadScript } from "@react-google-maps/api";
import { Libraries } from "@react-google-maps/api/dist/utils/make-load-script-url";
import ManualAddressDrawer from "_shared/addresses/_views/manual-address-drawer";
import Address, { InvalidAddressError } from "_shared/addresses/address";
import _, { debounce } from "lodash";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface PlaceAutocompleteProps {
  className?: string;
  label: string;
  value: Address | null;
  required?: boolean;
  variant?: "outlined" | "standard";
  requireValidation?: boolean;
  locationServicesDisabledRef?: React.RefObject<boolean>;
  onLocationServicesDisabledChange?: (disabled: boolean) => void;
  disableManualEntryWhenServicesUnavailable?: boolean;
  /**
   * Bypasses the Google Places API and returns a fixed list of fake
   * suggestions so the autocomplete dropdown behaviour can be verified without
   * a live API key or network access. Use for testing.
   */
  DEBUG_simulateSuggestions?: boolean;
  /**
   * This method is called when location services are enabled
   * and an address is selected, or when location services are
   * disabled and the manual input debounce is complete.
   * 
   * This callback is only registered initially, to reregister the handler, change the key
   * @returns ({@link Address} | null, undefined) when successful
   * @returns (null, errorMessage: string)
   */
  onChange: (value: Address | null, errorMessage?: string) => void;
}

type ManualEntryOption = { __isManualEntry: true };
type AnyOption = google.maps.places.AutocompleteSuggestion | ManualEntryOption;
const MANUAL_ENTRY_OPTION: ManualEntryOption = { __isManualEntry: true };
function isManualEntryOption(option: AnyOption | null | undefined): option is ManualEntryOption {
  return Boolean(option && (option as ManualEntryOption).__isManualEntry);
}

export default function PlaceAutocomplete(props: Readonly<PlaceAutocompleteProps>) {
  const [inputValue, setInputValue] = useState(props.value?.toString() ?? "");
  const [options, setOptions] = useState<AnyOption[]>([]);
  const [value, setValue] = useState<AnyOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const addressIsDirty = useRef<boolean>(false);
  // Always points to the latest props.onChange so the long-lived debouncedValidate
  // (which has an empty deps array) never calls a stale closure.
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;
  const locationServicesDisabledRef = props.locationServicesDisabledRef;
  const onLocationServicesDisabledChange = props.onLocationServicesDisabledChange;
  const disableManualEntryWhenServicesUnavailable = props.disableManualEntryWhenServicesUnavailable;
  const DEBUG_simulateSuggestions = props.DEBUG_simulateSuggestions ?? false;
  const simulationWarningText = "DEV MODE: Using simulated location service.";
  const unavailableServiceWarningText = "Location service is unavailable.";
  const simulationTintSx = useMemo(() => DEBUG_simulateSuggestions
    ? {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "warning.50",
      },
      "& .MuiInputBase-root": {
        backgroundColor: "warning.50",
      },
      "& .MuiFormHelperText-root": {
        color: "warning.dark",
      },
    }
    : undefined, [DEBUG_simulateSuggestions]);
  const [isManualAddressDrawerOpen, setIsManualAddressDrawerOpen] = useState(false);
  const [manualAddressAutofocus, setManualAddressAutofocus] = useState<boolean>(false);
  const manualInput = useRef<HTMLInputElement>(undefined);
  const suppressNextInputReset = useRef(false);

  // Backoff configuration
  const backoffRef = useRef({
    attempts: 0,
    maxAttempts: 5,
    baseDelay: 300,
  });

  const libraries = useRef<Libraries>(["places"]);
  const { isLoaded: placesApiLoaded, loadError: placesApiLoadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
    libraries: libraries.current,
  });
  const servicesUnavailable = Boolean(locationServicesDisabledRef?.current || placesApiLoadError);

  // Keep the request object in a ref so it persists between renders
  const requestRef = useRef<google.maps.places.AutocompleteRequest>({
    input: "",
    language: "en-US",
  });

  // Use a ref to track the latest request ID to avoid race conditions
  const newestRequestId = useRef(0);

  useEffect(() => {
    if(!manualAddressAutofocus) return;
    manualInput?.current?.focus();
    setManualAddressAutofocus(false);
  }, [manualAddressAutofocus])

  // Sync displayed text when props.value is set after initial mount
  // (e.g. async form load). Skipped if the user has already typed.
  useEffect(() => {
    if (addressIsDirty.current) return;
    setInputValue(props.value?.toString() ?? "");
  }, [props.value]);

  useEffect(() => {
    if (!servicesUnavailable || !disableManualEntryWhenServicesUnavailable) return;
    addressIsDirty.current = false;
    setInputValue(props.value?.toString() ?? "");
  }, [disableManualEntryWhenServicesUnavailable, props.value, servicesUnavailable]);

  // Once the API is loaded, refresh the token.
  useEffect(() => {
    if (placesApiLoaded && globalThis.google) {
      refreshToken();
    }
  }, [placesApiLoaded]);

  useEffect(() => {
    if (!placesApiLoadError) return;
    onLocationServicesDisabledChange?.(true);
  }, [onLocationServicesDisabledChange, placesApiLoadError]);

  // Refresh the session token
  const refreshToken = () => {
    requestRef.current.sessionToken = new globalThis.google.maps.places.AutocompleteSessionToken();
  };

  const isValidCoordinate = (value: number | undefined): boolean => {
    return !Number.isNaN(value) && Number.isFinite(value);
  };

  // Highlight matching text
  const highlightMatches = (text: string | undefined, query: string) => {
    if (!text || !query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? <strong key={btoa(part) + "-" + index}>{part}</strong> : part,
      );
  };

  // Helper function to safely extract address components
  const getAddressComponent = (
    components: google.maps.places.AddressComponent[] | undefined,
    type: string,
  ): string | undefined => {
    if (!components) return;
    const component = components.find((comp) => comp.types.includes(type));
    return component?.shortText ?? undefined;
  };

  // Handle OVER_QUERY_LIMIT with exponential backoff
  const handleOverQueryLimit = useCallback((value: string) => {
    console.warn("Google Places API rate limit exceeded");
    setErrorMessage("Too many requests. Please try again in a moment.");

    if (backoffRef.current.attempts < backoffRef.current.maxAttempts) {
      backoffRef.current.attempts++;
      const delay = backoffRef.current.baseDelay * Math.pow(2, backoffRef.current.attempts);
      console.debug(`Retrying in ${delay}ms (attempt ${backoffRef.current.attempts})`);
      globalThis.setTimeout(() => fetchSuggestionsRef.current(value), delay);
    } else {
      console.error("Maximum retry attempts reached");
      backoffRef.current.attempts = 0;
    }
  }, []);

  const fetchSuggestionsRef = useRef<(value: string) => void>(() => {});

  const fetchSuggestions = useCallback(
    async (value: string): Promise<void> => {
      if (locationServicesDisabledRef?.current) return;
      if (value === "") {
        setOptions([]);
        return;
      }

      if (DEBUG_simulateSuggestions) {
        setIsLoading(true);
        console.warn("Simulating suggestions with fixed data (DEV ONLY)");
        await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 200));
        setOptions(DEBUG_SIMULATED_SUGGESTIONS);
        setIsLoading(false);
        return;
      }

      // Update the request with the current input
      requestRef.current.input = value;
      const currentRequestId = ++newestRequestId.current;

      setIsLoading(true);
      try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          requestRef.current,
        );

        // If a newer request has been made, ignore this response
        if (currentRequestId !== newestRequestId.current) return;

        setOptions(suggestions);
        setErrorMessage(null);
        backoffRef.current.attempts = 0; // Reset backoff attempts on success
      } catch (error: unknown) {
        // If a newer request has been made, ignore this error
        if (currentRequestId !== newestRequestId.current) return;

        if ((error as { code: number }).code === 7) {
          // Unregistered user
          onLocationServicesDisabledChange?.(true);
          setManualAddressAutofocus(true);
          setErrorMessage("Location service is unavailable.");
          return;
        }

        console.error("Error fetching suggestions:", error);

        const { MapsRequestError, MapsServerError, MapsNetworkError } = (await google.maps.importLibrary(
          "core",
        )) as google.maps.CoreLibrary;

        if (error instanceof MapsRequestError) {
          if (error.code === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
            handleOverQueryLimit(value);
          } else {
            console.error("Google Places API request denied - check API key configuration");
            setErrorMessage("Location service is temporarily unavailable");
          }
        } else if (error instanceof MapsServerError || error instanceof MapsNetworkError) {
          console.error("Google Places API server or network error");
          setErrorMessage("Location service is temporarily unavailable");
        }

        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [handleOverQueryLimit, locationServicesDisabledRef, onLocationServicesDisabledChange, DEBUG_simulateSuggestions],
  );

  const debounceFetchSuggestions = useMemo(
    () => _.debounce((value: string) => fetchSuggestions(value), 300),
    [fetchSuggestions],
  );

  useEffect(() => {
    return () => debounceFetchSuggestions.cancel();
  }, [debounceFetchSuggestions]);

  const debouncedValidate = useMemo(() => 
    debounce((addressString: string, isDirty?: boolean) => {
      if(!isDirty) return;

      let address: Address | null = null;
      let errorMessage: string | undefined;

      try {
        address = addressString ? new Address({address1: addressString}) : null;
      } catch(error) {
        if(error instanceof InvalidAddressError) {
          errorMessage = error.message;
        }
        else {
          throw error;
        }
      }
      setErrorMessage(errorMessage ?? null);
      onChangeRef.current(address, errorMessage);
    }, 300), 
  []);

  useEffect(() => {
    if (disableManualEntryWhenServicesUnavailable && servicesUnavailable) return;
    if(!locationServicesDisabledRef?.current && !placesApiLoadError) return;
    debouncedValidate(inputValue, addressIsDirty.current);
    return () => debouncedValidate.cancel();
  }, [
    debouncedValidate,
    disableManualEntryWhenServicesUnavailable,
    inputValue,
    locationServicesDisabledRef,
    placesApiLoadError,
    servicesUnavailable,
  ]);

  // Handle input changes from the Autocomplete component
  const handleInputChange = (_event: SyntheticEvent, newInputValue: string, reason: string) => {
    const isManualFallbackMode = Boolean(locationServicesDisabledRef?.current || placesApiLoadError);

    // MUI fires onInputChange with reason="blur" (clearOnBlur) or reason="reset"
    // when the component loses focus with no selected suggestion. Don't let that
    // wipe out an address that was pre-loaded (i.e. the user hasn't edited it yet).
    if ((reason === "blur" || reason === "reset") && !addressIsDirty.current) {
      return;
    }

    // After the user picks "Enter address manually", MUI fires a reset that
    // would overwrite the typed text with the option label — suppress it.
    if (reason === "reset" && suppressNextInputReset.current) {
      suppressNextInputReset.current = false;
      return;
    }

    setInputValue(newInputValue);

    // Handle clearing logic
    if (reason === "clear" || (reason === "input" && !newInputValue)) {
      setOptions([]);
      setValue(null);
      props.onChange(null);
      return;
    }

    // Only fetch suggestions if the user is actively typing
    if (reason === "input") {
      addressIsDirty.current = true;
      debounceFetchSuggestions(newInputValue);

      // Only treat typed text as a committed value in manual fallback mode.
      if (isManualFallbackMode) {
        debouncedValidate(newInputValue, true);
      }
    }
  };

  // When a suggestion is selected, fetch additional place fields
  const onPlaceSelected = async (suggestion: google.maps.places.AutocompleteSuggestion | null) => {
    if (!suggestion) return;
    // Cancel any pending debouncedValidate so it doesn't overwrite the
    // full Places result with the partially-typed text after selection.
    debouncedValidate.cancel();

    if (DEBUG_simulateSuggestions) {
      console.warn("Simulated place selected:", suggestion);
      const meta = (suggestion as DEBUG_SimulatedSuggestion).__simulatedAddress;
      if (meta) {
        props.onChange(new Address(meta));
        setOptions([]);
        setInputValue(suggestion.placePrediction?.text.text ?? "");
      }
      return;
    }

    const placePrediction = suggestion.placePrediction;
    const place = placePrediction?.toPlace();

    if (!place) {
      console.error("Failed to convert place prediction to place");
      return;
    }

    await place.fetchFields({
      fields: ["addressComponents", "location"],
    });

    const streetNumber = getAddressComponent(place.addressComponents, "street_number");
    const route = getAddressComponent(place.addressComponents, "route");

    // Format street address properly handling empty components
    const streetAddress = [streetNumber, route].filter(Boolean).join(" ") || "";

    const lat = place.location?.lat();
    const lng = place.location?.lng();

    if (!isValidCoordinate(lat) || !isValidCoordinate(lng)) {
      console.warn("Invalid coordinates received from Google Places API", { lat, lng });
    }

    const newAddress = {
      address1: streetAddress,
      coordinates: {
        latitude: isValidCoordinate(lat) ? String(lat) : "0",
        longitude: isValidCoordinate(lng) ? String(lng) : "0",
      },
      city: "",
      state: "",
      country: "",
      postalCode: "",
    };

    for (const c of place.addressComponents ?? []) {
      if (c.types.includes("locality")) newAddress.city = c.shortText ?? "";
      if (c.types.includes("administrative_area_level_1")) newAddress.state = c.shortText ?? "";
      if (c.types.includes("country")) newAddress.country = c.shortText ?? "";
      if (c.types.includes("postal_code")) newAddress.postalCode = c.shortText ?? "";
    }

    props.onChange(new Address({ ...newAddress }));

    setOptions([]);
    setInputValue(suggestion.placePrediction?.text.text ?? "");
    refreshToken();
  };

  // Handle selection change from the Autocomplete component
  const handleChange = async (_event: SyntheticEvent, newValue: AnyOption | null) => {
    if (isManualEntryOption(newValue)) {
      // Proactively restore the input to the last committed address so the option
      // label "Enter address manually" never appears in the text field.
      setInputValue(props.value?.toString() ?? "");
      // Clear dirty so the props.value sync effect fires when the drawer is accepted.
      addressIsDirty.current = false;
      // Suppress MUI's follow-up reason="reset" call (which would also carry the label text).
      suppressNextInputReset.current = true;
      setIsManualAddressDrawerOpen(true);
      return;
    }
    setValue(newValue);
    setOptions(prevOptions => (newValue ? [newValue, ...prevOptions] : prevOptions));
    await onPlaceSelected(newValue);
  };

  // Convert a suggestion to a label string
  const getOptionLabel = (option: AnyOption) => {
    if (isManualEntryOption(option)) return "Enter address manually";
    return option.placePrediction?.text.text ?? "Unknown Location";
  };

  const closeManualAddressDrawer = () => {
    setIsManualAddressDrawerOpen(false);
  };

  const handleManualAddressAccept = (manualAddress: Address) => {
    addressIsDirty.current = false;
    setIsManualAddressDrawerOpen(false);
    setValue(null);
    setOptions([]);
    setErrorMessage(null);
    setInputValue(manualAddress.toString());
    props.onChange(manualAddress);
  };

  const renderPlacesApiLoadError = useCallback(() => {
    const manualEntryDisabled = Boolean(disableManualEntryWhenServicesUnavailable);
    const displayedValue = manualEntryDisabled ? props.value?.toString() ?? "" : inputValue;
    const hasCommittedValue = manualEntryDisabled && Boolean(displayedValue);
    const showError = !hasCommittedValue;

    return (
      <TextField
        error={showError}
        sx={{
          ...(showError ? {
            "& .MuiInputLabel-root.Mui-error": {
              color: "warning.main",
              borderColor: "warning.main",
            },
            "& .MuiFormHelperText-root.Mui-error": {
              color: "warning.main",
            },
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "warning.main",
            },
          } : {}),
          ...(simulationTintSx ?? {}),
        }}
        className={props.className}
        fullWidth
        inputRef={manualInput}
        label={props.label}
        variant={props.variant}
        value={displayedValue}
        onChange={(event) => {
          if (manualEntryDisabled) return;

          setInputValue(prev => {
            if(addressIsDirty.current) return event.target.value;
            if(prev !== event.target.value)
            {
              addressIsDirty.current = true;
              return event.target.value;
            }
            return prev;
          })
        }}
        disabled={props.requireValidation}
        helperText={
          DEBUG_simulateSuggestions
            ? simulationWarningText
            : manualEntryDisabled
            ? (
              <>
                {unavailableServiceWarningText}{" "}
                <Link
                  component="button"
                  variant="inherit"
                  underline="always"
                  onClick={() => setIsManualAddressDrawerOpen(true)}
                >
                  Edit Manually
                </Link>
              </>
            )
            : "Location service is temporarily unavailable"
        }
        slotProps={{
          input: {
            readOnly: manualEntryDisabled,
          },
        }}
      />
    );
  }, [
    disableManualEntryWhenServicesUnavailable,
    inputValue,
    DEBUG_simulateSuggestions,
    props.className,
    props.label,
    props.requireValidation,
    props.value,
    props.variant,
    simulationTintSx,
    unavailableServiceWarningText,
    simulationWarningText,
  ]);

  let content;

  if (placesApiLoadError || locationServicesDisabledRef?.current) {
    content = renderPlacesApiLoadError();
  } else if (!placesApiLoaded && !locationServicesDisabledRef?.current && !placesApiLoadError) {
    content = (
      <TextField
        className={props.className}
        fullWidth
        label={props.label}
        variant={props.variant}
        disabled
        error={Boolean(errorMessage)}
        helperText={errorMessage}
        slotProps={{
          input: {
            endAdornment: <CircularProgress color="inherit" disableShrink size={20} />,
          },
        }}
      />
    );
  } else {
    content = (
      <Autocomplete
      sx={simulationTintSx}
      getOptionLabel={getOptionLabel}
      filterOptions={(x) => [...x, MANUAL_ENTRY_OPTION]}
      slots={{
        paper: PlaceResultsPaper,
      }}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      loading={isLoading}
      inputValue={inputValue}
      noOptionsText="No locations"
      onChange={handleChange}
      clearOnBlur={true}
      clearOnEscape={true}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          className={props.className}
          fullWidth
          sx={simulationTintSx}
          error={Boolean(errorMessage)}
          helperText={errorMessage ?? (DEBUG_simulateSuggestions ? simulationWarningText : undefined)}
          required={props.required}
          label={props.label}
          variant={props.variant}
          slotProps={{
            inputLabel: {
              shrink: Boolean(inputValue || value),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        if (isManualEntryOption(option)) {
          return (
            <li key="__manual-entry" {...optionProps}>
              <Grid container sx={{ alignItems: "center" }}>
                <Grid sx={{ display: "flex", width: 44 }}>
                  <EditIcon sx={{ color: "text.secondary" }} />
                </Grid>
                <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                  <Typography variant="body1" noWrap>
                    Enter address manually
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }

        const name = option.placePrediction?.text.text;
        const description = option.placePrediction?.secondaryText?.text;

        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                <Typography variant="body1" noWrap>
                  {highlightMatches(name, inputValue ?? "")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {description}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
    );
  }

  return (
    <>
      {content}
      {isManualAddressDrawerOpen && (
        <ManualAddressDrawer
          open={true}
          value={props.value}
          onClose={closeManualAddressDrawer}
          onAccept={handleManualAddressAccept}
        />
      )}
    </>
  );
}

// #region Simulation helpers (DEV ONLY)
type DEBUG_SimulatedSuggestion = google.maps.places.AutocompleteSuggestion & {
  __simulatedAddress: {
    address1: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    coordinates: { latitude: string; longitude: string };
  };
};

function DEBUG_makeSimulatedSuggestion(
  label: string,
  address1: string,
  city: string,
  state: string,
  postalCode: string,
  country: string,
): DEBUG_SimulatedSuggestion {
  return {
    __simulatedAddress: { address1, city, state, postalCode, country, coordinates: { latitude: "0", longitude: "0" } },
    placePrediction: {
      text: { text: label } as google.maps.places.FormattableText,
      secondaryText: { text: `${city}, ${state} ${postalCode}` } as google.maps.places.FormattableText,
    } as google.maps.places.PlacePrediction,
  } as DEBUG_SimulatedSuggestion;
}

const DEBUG_SIMULATED_SUGGESTIONS: DEBUG_SimulatedSuggestion[] = [
  DEBUG_makeSimulatedSuggestion("123 Main St, Springfield, IL 62701", "123 Main St", "Springfield", "IL", "62701", "US"),
  DEBUG_makeSimulatedSuggestion("456 Oak Ave, Reading, PA 19555", "456 Oak Ave", "Reading", "PA", "19555", "US"),
  DEBUG_makeSimulatedSuggestion("789 Elm Rd, Capital City, CA 88999", "789 Elm Rd", "Capital City", "CA", "88999", "US"),
];
// #endregion

function PlaceResultsPaper(props: PaperProps) {
  const theme = useTheme();

  return (
    <Paper {...props}>
      {props.children}
      {/* Legal requirement https://developers.google.com/maps/documentation/javascript/policies#logo */}
      <Box
        sx={(staticTheme) => ({
          display: "flex",
          justifyContent: "flex-end",
          p: 1,
          pt: "1px",
          ...staticTheme.applyStyles("dark", {
            opacity: 0.8,
          }),
        })}
      >
        <img
          src={
            theme.palette.mode === "dark"
              ? "https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white3_hdpi.png"
              : "https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
          }
          alt="Powered by Google"
          width="120"
          height="14"
        />
      </Box>
    </Paper>
  );
}
